import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, CreditCard, Heart, DollarSign, User, MapPin, Calendar } from 'lucide-react';

const PaymentPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchBookingInfo();
  }, [bookingId]);

  const fetchBookingInfo = async () => {
    try {
      const response = await fetch(`${API}/api/payments/booking/${bookingId}`);
      if (response.ok) {
        const data = await response.json();
        setBookingInfo(data);
      }
    } catch (error) {
      console.error('Failed to load booking info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalPayment = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/api/payments/create-final-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          origin_url: window.location.origin,
          tip_amount: parseFloat(tipAmount) || 0
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        alert('Failed to create payment. Please try again.');
      }
    } catch (error) {
      alert('Payment error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!bookingInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold mb-4">Booking Not Found</h2>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const { booking, deposit_amount, remaining_balance, transactions } = bookingInfo;
  const tip = parseFloat(tipAmount) || 0;
  const totalDue = remaining_balance + tip;
  const isFullyPaid = booking.payment_status === 'fully_paid';
  const hasDepositPaid = booking.payment_status === 'deposit_paid' || isFullyPaid;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-8" data-testid="payment-page-title">Tour Payment</h1>

        {/* Booking Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Booking Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-gray-700">
              <User className="w-4 h-4" />
              <span>Guide: <strong>{booking.guide_name}</strong></span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <MapPin className="w-4 h-4" />
              <span>{booking.school_name}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <Calendar className="w-4 h-4" />
              <span>{booking.preferred_date}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CreditCard className="w-4 h-4" />
              <span>Tour Type: {booking.tour_type}</span>
            </div>
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm">
                <span>Total Tour Price</span>
                <span className="font-bold">${booking.price?.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Status */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Payment Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">10% Deposit</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold">${deposit_amount.toFixed(2)}</span>
                {hasDepositPaid ? (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Paid
                  </span>
                ) : (
                  <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">Pending</span>
                )}
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Remaining Balance</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold">${remaining_balance.toFixed(2)}</span>
                {isFullyPaid ? (
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Paid
                  </span>
                ) : (
                  <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">Due after tour</span>
                )}
              </div>
            </div>
            {isFullyPaid && booking.tip_amount > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tip</span>
                <span className="font-semibold text-green-600">${booking.tip_amount.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pay Remaining + Tip */}
        {hasDepositPaid && !isFullyPaid && (
          <Card className="mb-6 border-2 border-black" data-testid="final-payment-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Complete Your Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span>Remaining Balance</span>
                  <span className="font-bold">${remaining_balance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <Label htmlFor="tip" className="flex items-center gap-1">
                    <Heart className="w-4 h-4 text-pink-500" /> Add a Tip (optional)
                  </Label>
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    <Input
                      id="tip"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value)}
                      className="w-24 text-right"
                      data-testid="tip-input"
                    />
                  </div>
                </div>
                <div className="border-t pt-2 flex justify-between text-lg font-bold">
                  <span>Total Due</span>
                  <span>${totalDue.toFixed(2)}</span>
                </div>
              </div>

              {/* Quick tip buttons */}
              <div className="flex gap-2">
                {[5, 10, 15, 20].map(amount => (
                  <button
                    key={amount}
                    onClick={() => setTipAmount(String(amount))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                      tipAmount === String(amount)
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                    }`}
                    data-testid={`tip-${amount}`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleFinalPayment}
                disabled={isSubmitting}
                className="w-full py-6 text-lg bg-black hover:bg-gray-800"
                data-testid="pay-remaining-btn"
              >
                {isSubmitting ? 'Redirecting to payment...' : `Pay $${totalDue.toFixed(2)}`}
              </Button>
            </CardContent>
          </Card>
        )}

        {isFullyPaid && (
          <Card className="border-green-200 bg-green-50" data-testid="fully-paid-card">
            <CardContent className="p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-800">Fully Paid</h3>
              <p className="text-green-600 text-sm mt-1">Thank you for choosing We.Tour!</p>
            </CardContent>
          </Card>
        )}

        {!hasDepositPaid && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-6 text-center">
              <p className="text-yellow-800 font-medium">Deposit payment is still pending.</p>
              <p className="text-yellow-600 text-sm mt-1">Please complete your deposit to confirm the booking.</p>
            </CardContent>
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default PaymentPage;
